import { castArray, isNil } from 'lodash-es';
import { api } from '@/api';
import { RouteDurationsParams, RoutePointType } from '@/api/apis/mission-planner-distance';
import { AviaLegTypes, GroundLegTypes, RouteLegTypeEnum, RouteLocationType } from '@/api/apis/route';

// Ground legs generated from suggested route Drive actions, which come without a distance.
const DRIVE_ACTION_LEG_TYPES = [RouteLegTypeEnum.TmdxAffiliateGroundLeg, RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg];

type LegPoints = {
  legId: string;
  origin: RoutePointType;
  destination: RoutePointType;
};

const getPointKey = ({ latitude, longitude }: RoutePointType) => `${latitude},${longitude}`;

const getPairKey = (origin: RoutePointType, destination: RoutePointType) =>
  `${getPointKey(origin)}|${getPointKey(destination)}`;

const getLocationPoint = (locationId: string, locations: RouteLocationType[]): RoutePointType | null => {
  const routeLocation = locations.find((l) => l.id === locationId);
  const { latitude, longitude } = routeLocation?.location ?? {};

  if (isNil(latitude) || isNil(longitude)) return null;

  return { latitude, longitude };
};

const getLegPoints = (leg: AviaLegTypes | GroundLegTypes, locations: RouteLocationType[]): LegPoints | null => {
  if (!DRIVE_ACTION_LEG_TYPES.includes(leg.legType) || (leg as GroundLegTypes).distanceInMeters) return null;

  const origin = getLocationPoint(leg.departureLocation.locationId, locations);
  const destination = getLocationPoint(leg.arrivalLocation.locationId, locations);

  return origin && destination ? { legId: leg.id, origin, destination } : null;
};

const groupByOrigin = (legs: LegPoints[]): RouteDurationsParams[] => {
  const groups = legs.reduce<Map<string, { origin: RoutePointType; destinations: Map<string, RoutePointType> }>>(
    (acc, { origin, destination }) => {
      const group = acc.get(getPointKey(origin)) ?? { origin, destinations: new Map() };

      group.destinations.set(getPointKey(destination), destination);

      return acc.set(getPointKey(origin), group);
    },
    new Map(),
  );

  return [...groups.values()].map(({ origin, destinations }) => ({ origin, destinations: [...destinations.values()] }));
};

export const fetchGroundLegDistances = async (
  legs: (AviaLegTypes | GroundLegTypes)[],
  locations: RouteLocationType[],
): Promise<Map<string, number>> => {
  const legPoints = legs.map((leg) => getLegPoints(leg, locations)).filter((leg): leg is LegPoints => !!leg);

  if (!legPoints.length) return new Map();

  const body = groupByOrigin(legPoints);
  const response = await api.missionPlannerDistance.getDistances(body);
  const distances = castArray(response).flatMap((r) => r.distances);
  const pairKeys = body.flatMap(({ origin, destinations }) => destinations.map((d) => getPairKey(origin, d)));
  const distanceByPair = new Map(pairKeys.map((key, index) => [key, distances[index]]));

  return new Map(
    legPoints.flatMap(({ legId, origin, destination }) => {
      const distance = distanceByPair.get(getPairKey(origin, destination));

      return isNil(distance) || distance < 0 ? [] : [[legId, distance] as const];
    }),
  );
};
